'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import * as React from 'react';
import { useStore } from '@base-ui/utils/store';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import MuiDialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import { inputBaseClasses } from '@mui/material/InputBase';
import TextField from '@mui/material/TextField';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useSchedulerStoreContext } from '@mui/x-scheduler-internals/use-scheduler-store-context';
import { useAdapterContext } from '@mui/x-scheduler-internals/use-adapter-context';
import { schedulerEventSelectors, schedulerOccurrencePlaceholderSelectors, schedulerOtherSelectors, schedulerRecurringEventSelectors } from '@mui/x-scheduler-internals/scheduler-selectors';
import { useEventDialogStyledContext } from "./EventDialogStyledContext.mjs";
import { useEventDialogOptionalRenderers } from "./EventDialogOptionalRenderersContext.mjs";
import { computeRange, hasProp, validateRange } from "./utils.mjs";
import EventDialogHeader from "./EventDialogHeader.mjs";
import { GeneralTab } from "./GeneralTab.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const FormActions = styled(DialogActions, {
  name: 'MuiEventDialog',
  slot: 'FormActions'
})(({
  theme
}) => ({
  padding: theme.spacing(3),
  gap: theme.spacing(2)
}));
const DialogContent = styled(MuiDialogContent, {
  name: 'MuiEventDialog',
  slot: 'DialogContent'
})({
  cursor: 'default',
  userSelect: 'text',
  padding: 0,
  minWidth: 360,
  width: 450,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden'
});
const EventDialogTitleTextField = styled(TextField, {
  name: 'MuiEventDialog',
  slot: 'TitleTextField'
})(({
  theme
}) => ({
  flex: 1,
  [`& .${inputBaseClasses.root}`]: {
    fontSize: theme.typography.h6.fontSize,
    lineHeight: theme.typography.h6.lineHeight,
    fontWeight: theme.typography.h6.fontWeight
  }
}));
const EventDialogForm = styled('form', {
  name: 'MuiEventDialog',
  slot: 'Form'
})({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0
});
const EventDialogTabsContainer = styled('div', {
  name: 'MuiEventDialog',
  slot: 'TabsContainer'
})(({
  theme
}) => ({
  borderBottom: `1px solid ${(theme.vars || theme).palette.divider}`
}));
const EventDialogTabs = styled(Tabs, {
  name: 'MuiEventDialog',
  slot: 'Tabs'
})(({
  theme
}) => ({
  padding: theme.spacing(0, 3)
}));
export function FormContent(props) {
  const {
    occurrence,
    onClose,
    dragHandlerRef
  } = props;

  // Context hooks
  const adapter = useAdapterContext();
  const {
    schedulerId,
    classes,
    localeText
  } = useEventDialogStyledContext();
  const store = useSchedulerStoreContext();

  // Selector hooks
  const isPropertyReadOnly = useStore(store, schedulerEventSelectors.isPropertyReadOnly, occurrence.id);
  const rawPlaceholder = useStore(store, schedulerOccurrencePlaceholderSelectors.value);
  const recurringEventsPlugin = useStore(store, schedulerOtherSelectors.recurringEventsPlugin);
  const displayTimezone = useStore(store, schedulerOtherSelectors.displayTimezone);
  const showRecurrence = useStore(store, schedulerOtherSelectors.areRecurringEventsAvailable);
  const shouldEventRequireResource = useStore(store, schedulerOtherSelectors.shouldEventRequireResource);

  // Optional renderer hooks
  const {
    recurrenceTab: RecurrenceTabRenderer
  } = useEventDialogOptionalRenderers();
  const recurrencePresets = useStore(store, schedulerRecurringEventSelectors.presets, occurrence.displayTimezone.start);
  const defaultRecurrencePresetKey = useStore(store, schedulerRecurringEventSelectors.defaultPresetKey, occurrence.displayTimezone.rrule, occurrence.displayTimezone.start);
  const titleInputRef = React.useCallback(input => input?.focus(), []);

  // State hooks
  const [tabValue, setTabValue] = React.useState('general');
  const [errors, setErrors] = React.useState({});
  const [controlled, setControlled] = React.useState(() => {
    const fmtDate = d => adapter.formatByString(d.value, 'yyyy-MM-dd');
    const fmtTime = d => adapter.formatByString(d.value, 'HH:mm');
    const base = occurrence.displayTimezone.rrule;
    return {
      startDate: fmtDate(occurrence.displayTimezone.start),
      endDate: fmtDate(occurrence.displayTimezone.end),
      startTime: fmtTime(occurrence.displayTimezone.start),
      endTime: fmtTime(occurrence.displayTimezone.end),
      resourceId: occurrence.resource ?? null,
      allDay: !!occurrence.allDay,
      color: hasProp(occurrence, 'color') ? occurrence.color : null,
      recurrenceSelection: defaultRecurrencePresetKey,
      rruleDraft: _extends({
        freq: base?.freq ?? 'WEEKLY',
        interval: base?.interval ?? 1,
        byDay: base?.byDay ?? [],
        byMonthDay: base?.byMonthDay ?? []
      }, base?.count ? {
        count: base.count
      } : {}, base?.until ? {
        until: base.until
      } : {})
    };
  });
  const handleSubmit = async event => {
    event.preventDefault();
    const {
      start,
      end
    } = computeRange(adapter, controlled, displayTimezone);
    const form = new FormData(event.currentTarget);
    setErrors({});
    const err = validateRange(adapter, start, end, controlled.allDay);
    if (err) {
      setErrors({
        [err.field]: localeText.startDateAfterEndDateError
      });
      return;
    }
    if (shouldEventRequireResource && controlled.resourceId === null) {
      setErrors({
        resource: localeText.requiredResourceError
      });
      return;
    }
    const metaChanges = {
      title: form.get('title').trim(),
      description: form.get('description').trim(),
      allDay: controlled.allDay,
      resource: controlled.resourceId === null ? undefined : controlled.resourceId,
      color: controlled.color === null ? undefined : controlled.color
    };
    let rruleToSubmit;
    if (!showRecurrence || !recurrencePresets) {
      rruleToSubmit = undefined;
    } else if (controlled.recurrenceSelection === null) {
      rruleToSubmit = undefined;
    } else if (controlled.recurrenceSelection === 'custom') {
      rruleToSubmit = controlled.rruleDraft;
    } else {
      rruleToSubmit = recurrencePresets[controlled.recurrenceSelection];
    }
    if (rawPlaceholder?.type === 'creation') {
      store.createEvent(_extends({}, metaChanges, {
        start,
        end,
        rrule: rruleToSubmit
      }));
    } else if (showRecurrence && recurringEventsPlugin && occurrence.displayTimezone.rrule) {
      const recurrenceModified = !schedulerRecurringEventSelectors.isSameRRule(store.state, occurrence.displayTimezone.rrule, rruleToSubmit);
      const changes = _extends({}, metaChanges, {
        id: occurrence.id,
        start,
        end
      }, recurrenceModified ? {
        rrule: rruleToSubmit
      } : {});
      await store.updateRecurringEvent({
        occurrenceStart: occurrence.displayTimezone.start.value,
        changes,
        onSubmit: onClose
      });

      // don't close the dialog
      return;
    } else {
      store.updateEvent(_extends({
        id: occurrence.id
      }, metaChanges, {
        start,
        end,
        rrule: rruleToSubmit
      }));
    }
    onClose();
  };
  const handleDelete = () => {
    if (showRecurrence && recurringEventsPlugin && occurrence.displayTimezone.rrule) {
      store.deleteRecurringEvent({
        occurrenceStart: occurrence.displayTimezone.start.value,
        eventId: occurrence.id,
        onSubmit: onClose
      });

      // don't close the dialog
      return;
    }
    store.deleteEvent(occurrence.id);
    onClose();
  };
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  return /*#__PURE__*/_jsx(DialogContent, {
    className: classes.eventDialogContent,
    children: /*#__PURE__*/_jsxs(EventDialogForm, {
      onSubmit: handleSubmit,
      className: classes.eventDialogForm,
      children: [/*#__PURE__*/_jsxs(EventDialogHeader, {
        onClose: onClose,
        dragHandlerRef: dragHandlerRef,
        children: [/*#__PURE__*/_jsx("span", {
          id: `${schedulerId}-event-dialog-title`,
          style: {
            position: 'absolute',
            width: 0,
            height: 0,
            overflow: 'hidden'
          },
          children: occurrence.title
        }), /*#__PURE__*/_jsx(EventDialogTitleTextField, {
          name: "title",
          defaultValue: occurrence.title,
          required: true,
          inputRef: titleInputRef,
          slotProps: {
            input: {
              readOnly: isPropertyReadOnly('title'),
              'aria-label': localeText.eventTitleAriaLabel
            },
            formHelperText: {
              role: 'alert'
            }
          },
          error: !!errors.title,
          helperText: errors.title,
          fullWidth: true,
          size: "small"
        })]
      }), showRecurrence && RecurrenceTabRenderer && /*#__PURE__*/_jsx(EventDialogTabsContainer, {
        className: classes.eventDialogTabsContainer,
        children: /*#__PURE__*/_jsxs(EventDialogTabs, {
          value: tabValue,
          onChange: handleTabChange,
          children: [/*#__PURE__*/_jsx(Tab, {
            id: `${schedulerId}-general-tab`,
            className: classes.eventDialogTab,
            label: localeText.generalTabLabel,
            value: "general"
          }), /*#__PURE__*/_jsx(Tab, {
            id: `${schedulerId}-recurrence-tab`,
            className: classes.eventDialogTab,
            label: localeText.recurrenceTabLabel,
            value: "recurrence"
          })]
        })
      }), /*#__PURE__*/_jsx(GeneralTab, {
        occurrence: occurrence,
        errors: errors,
        setErrors: setErrors,
        controlled: controlled,
        setControlled: setControlled,
        value: showRecurrence && RecurrenceTabRenderer ? tabValue : 'general'
      }), showRecurrence && RecurrenceTabRenderer && /*#__PURE__*/_jsx(RecurrenceTabRenderer, {
        occurrence: occurrence,
        controlled: controlled,
        setControlled: setControlled,
        tabValue: tabValue
      }), /*#__PURE__*/_jsx(Divider, {
        className: classes.eventDialogFormDivider
      }), /*#__PURE__*/_jsxs(FormActions, {
        className: classes.eventDialogFormActions,
        children: [/*#__PURE__*/_jsx(Button, {
          className: classes.eventDialogDeleteButton,
          color: "error",
          type: "button",
          onClick: handleDelete,
          children: localeText.deleteEvent
        }), /*#__PURE__*/_jsx(Button, {
          className: classes.eventDialogSaveButton,
          variant: "contained",
          type: "submit",
          children: localeText.saveChanges
        })]
      })]
    })
  });
}